import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, AlertTriangle, CheckCircle2, Activity, ShieldAlert, Ban } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import useSEO from "@/hooks/useSEO";
import { therapies, therapyJsonLd } from "@/data/therapies";

export default function TherapyPage() {
  const { slug } = useParams();
  const th = therapies[slug];
  const [open, setOpen] = useState(false);

  useSEO({
    title: th?.seoTitle,
    description: th?.seoDescription,
    image: th?.image,
    jsonLd: th ? therapyJsonLd(th) : undefined,
  });

  if (!th) return <Navigate to="/neuromodulation" replace />;

  return (
    <div data-testid={`therapy-page-${th.slug}`}>
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-14">
        <nav className="text-sm text-[#A39171] mb-8 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-[#E07A5F]">Home</Link> /
          <Link to="/neuromodulation" className="hover:text-[#E07A5F]">Neuromodulation</Link> /
          <span className="text-[#5C524E]">{th.procName}</span>
        </nav>
        <p className="eyebrow text-[#A39171] mb-6">{th.eyebrow}</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-4xl">
          {th.h1a} <span className="italic text-[#E07A5F]">{th.h1b}</span>
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-3xl leading-relaxed">{th.intro}</p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Button data-testid="th-hero-book" onClick={() => setOpen(true)} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium">
            Discuss Eligibility <ArrowUpRight className="ml-1 w-4 h-4" />
          </Button>
          <a href="tel:+919427560101" data-testid="th-hero-call" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] text-[#2D2623] px-8 h-14 inline-flex items-center justify-center font-medium transition-colors">
            Call +91 94275 60101
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-24">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9] relative">
          <img src={th.image} alt={th.imageAlt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/45 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 md:left-12 md:right-auto md:max-w-xl">
            <p className="eyebrow text-[#F0CF5B] mb-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">Neuromodulation Suite · Bhavnagar</p>
            <p className="font-display text-2xl md:text-3xl leading-tight text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
              {th.caption}
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">How it works</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            The science, <span className="italic text-[#E07A5F]">without the mystery</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {th.mechanism.map((m, i) => (
              <div key={m.title} data-testid={`th-mechanism-${i}`}
                className="bg-[#F4EFE6] rounded-3xl p-8 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-all">
                <span className="font-display text-3xl text-[#E07A5F] mb-4 block">0{i + 1}</span>
                <h3 className="font-display text-2xl text-[#2D2623] tracking-tight leading-tight">{m.title}</h3>
                <p className="text-[#5C524E] mt-3 text-sm leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDICATIONS */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">Who it is for</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            Where this treatment <span className="italic text-[#E07A5F]">genuinely helps</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {th.indicationGroups.map((g, i) => (
              <div key={g.label} data-testid={`th-indications-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 md:p-10 border border-[#A39171]/15">
                <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight mb-6">{g.label}</h3>
                <ul className="space-y-3">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-[#5C524E]">
                      <Activity className="w-4 h-4 text-[#E07A5F] mt-1 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSE */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">What a course looks like</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14">
            Step by step, <span className="italic text-[#E07A5F]">start to finish</span>.
          </h2>
          <ol className="space-y-10">
            {th.course.map((s, i) => (
              <li key={s.title} data-testid={`th-step-${i}`} className="grid grid-cols-12 gap-6 border-b border-[#A39171]/15 pb-10 last:border-none">
                <div className="col-span-12 md:col-span-3"><span className="font-display text-4xl text-[#E07A5F]">0{i + 1}</span></div>
                <div className="col-span-12 md:col-span-9">
                  <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight">{s.title}</h3>
                  <p className="text-[#5C524E] mt-3 text-lg leading-relaxed">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SAFETY */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">Safety</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            Side-effects and limits, <span className="italic text-[#E07A5F]">stated plainly</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div data-testid="th-side-effects" className="bg-[#FDFBF7] rounded-3xl p-8 md:p-10 border border-[#A39171]/15">
              <span className="w-12 h-12 rounded-2xl bg-[#E2C044] text-[#2D2623] flex items-center justify-center mb-5">
                <ShieldAlert className="w-5 h-5" />
              </span>
              <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight mb-6">Possible side-effects</h3>
              <ul className="space-y-3">
                {th.sideEffects.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-[#5C524E]">
                    <CheckCircle2 className="w-4 h-4 text-[#E07A5F] mt-1 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="th-not-for" className="bg-[#FDFBF7] rounded-3xl p-8 md:p-10 border border-[#A39171]/15">
              <span className="w-12 h-12 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center mb-5">
                <Ban className="w-5 h-5" />
              </span>
              <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight mb-6">When we will not do it</h3>
              <ul className="space-y-3">
                {th.notFor.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-[#5C524E]">
                    <CheckCircle2 className="w-4 h-4 text-[#E07A5F] mt-1 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div data-testid="th-safety-note" className="mt-14 bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start">
            <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </span>
            <div>
              <p className="eyebrow text-[#E2C044] mb-3">Please read this</p>
              <p className="text-lg leading-relaxed text-[#FDFBF7]/90">{th.safetyNote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">Frequently asked</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            What patients and families <span className="italic text-[#E07A5F]">always ask</span>.
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {th.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`th-${i}`} data-testid={`th-faq-${i}`}
                className="bg-[#F4EFE6] rounded-2xl border border-[#A39171]/15 px-6 data-[state=open]:border-[#E07A5F]/35 transition-colors">
                <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-[#2D2623] tracking-tight hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C524E] text-base md:text-lg leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* RELATED + CTA */}
      <section className="bg-[#2D2623] text-[#FDFBF7] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
              Find out if you are <span className="italic text-[#E07A5F]">a candidate</span>.
            </h2>
            <p className="text-[#FDFBF7]/70 mt-5 max-w-xl">
              A 30-minute eligibility consultation at Kalanala, Bhavnagar. Honest answers, written costs, no pressure.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button data-testid="th-cta-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium">Request Appointment</Button>
              <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors">Call +91 94275 60101</a>
            </div>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <p className="eyebrow text-[#E2C044] mb-1">Related</p>
            {th.related.map((r) => (
              <Link key={r.to} to={r.to} data-testid={`th-related-${r.to.replace(/\//g, "-")}`}
                className="rounded-2xl bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 hover:bg-[#E07A5F] px-6 py-4 flex items-center justify-between transition-colors">
                <span className="font-medium">{r.label}</span>
                <ArrowUpRight className="w-4 h-4 shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
